import { ReactElement, ReactNode, useRef, useState } from 'react'
import { SectionCard } from 'app/components/SectionCard'
import { Button } from 'app/components/ui/button'
import { Label } from 'app/components/ui/label'
import { Switch } from 'app/components/ui/switch'
import { TokenEditor, type TokenEditorHandle } from 'app/components/workflows/inspectors/TokenEditor'
import { DEFAULT_CONFIRMATION_MESSAGE, OPT_OUT_SUFFIX, renderConfirmationSample } from 'app/lib/aiAgentSmsConfirmation'
import {
  DEFAULT_GREETING,
  renderGreeting,
  POST_BOOKING_TAGS,
  postBookingFromEditor,
  postBookingToEditor,
  renderPostBookingSample,
} from 'app/lib/aiAgentOverview'
import type { PhraseRule } from 'app/lib/aiAgentPhraseRules'
import { useAIAgentStore } from 'app/stores/StoreContext'
import { DEFAULT_HYGIENE_POST_BOOKING_MESSAGE, DEFAULT_POST_BOOKING_MESSAGE } from 'lib/aiAgent/postBookingDefaults'
import { AiAgentPhraseRules } from './AiAgentPhraseRules'
import { useSettingsSection } from './useSettingsSave'

const CONFIRMATION_HINT = `On by default. Right after the agent books a visit, the patient gets this text with the day and time. Turn it off if your practice software already sends its own confirmation.`
/** A post-booking line as it saves: its default is stored as blank, so the agent keeps following it. */
const storedPostBooking = (message: string, fallback = DEFAULT_POST_BOOKING_MESSAGE) =>
  message.trim() === fallback ? `` : message.trim()

/** Scripting's editors keep the gray border on focus, like every Input on the page. */
const INPUT_FOCUS = `focus:border-default`

const GREETING_TOKENS = [
  { key: `businessName`, label: `Business name` },
  { key: `botname`, label: `Agent name` },
]

type Script = {
  greeting: string
  postBookingMessage: string
  hygienePostBookingMessage: string
  confirmationOn: boolean
}

/** One script: its editor on the left, and on the right what callers hear or patients read. */
function ScriptRow({
  label,
  action,
  editor,
  preview,
  note,
}: {
  label: ReactNode
  action?: ReactNode
  editor: ReactNode
  preview?: ReactNode
  note?: ReactNode
}): ReactElement {
  return (
    <div className="grid gap-x-6 gap-y-4 border-t border-default pt-5 first:border-t-0 first:pt-0 md:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex min-h-7 items-center justify-between gap-2 text-label-sm text-default">
          {label}
          {action}
        </div>
        {editor}
      </div>
      {preview != null && (
        <div className="flex min-w-0 flex-col gap-2">
          <span className="flex min-h-7 items-center text-label-sm text-subtle">Preview</span>
          <p className="rounded-lg bg-surface-muted px-3 py-2 text-body-sm italic text-default">{preview}</p>
          {note && <span className="text-body-xs text-subtle">{note}</span>}
        </div>
      )}
    </div>
  )
}

function InsertBar({
  tokens,
  onInsert,
}: {
  tokens: { key: string; label: string }[]
  onInsert: (key: string) => void
}): ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-body-xs text-subtle">Insert</span>
      {tokens.map((token) => (
        <Button key={token.key} type="button" variant="white" size="xs" onClick={() => onInsert(token.key)}>
          {token.label}
        </Button>
      ))}
    </div>
  )
}

/**
 * The agent's wording a practice controls: the lines it speaks and whether the confirmation text goes out.
 */
export function AiAgentScriptingSection({
  locationId,
  greeting,
  agentName,
  businessName,
  postBookingMessage,
  hygienePostBookingMessage,
  confirmationOn,
  practiceName,
  officePhone,
  phraseRules,
}: {
  locationId: string
  /** The greeting as stored, a template with {{businessName}} and {{botname}}. */
  greeting: string
  agentName: string
  businessName: string
  postBookingMessage: string
  hygienePostBookingMessage: string
  confirmationOn: boolean
  practiceName: string
  officePhone?: string
  phraseRules: PhraseRule[]
}): ReactElement {
  const agentStore = useAIAgentStore()
  const saved: Script = {
    greeting,
    postBookingMessage,
    hygienePostBookingMessage,
    confirmationOn,
  }
  const [draft, setDraft] = useState<Script | null>(null)
  const postBookingEditor = useRef<TokenEditorHandle>(null)
  const greetingEditor = useRef<TokenEditorHandle>(null)
  const hygieneEditor = useRef<TokenEditorHandle>(null)

  const shown: Script = draft ?? {
    ...saved,
    postBookingMessage: saved.postBookingMessage || DEFAULT_POST_BOOKING_MESSAGE,
    hygienePostBookingMessage: saved.hygienePostBookingMessage || DEFAULT_HYGIENE_POST_BOOKING_MESSAGE,
  }
  const preview = renderConfirmationSample(DEFAULT_CONFIRMATION_MESSAGE, { name: practiceName, phone: officePhone })
  const greetingChanged = shown.greeting.trim() !== saved.greeting.trim()
  const postBookingChanged = storedPostBooking(shown.postBookingMessage) !== storedPostBooking(saved.postBookingMessage)
  const hygieneChanged =
    storedPostBooking(shown.hygienePostBookingMessage, DEFAULT_HYGIENE_POST_BOOKING_MESSAGE) !==
    storedPostBooking(saved.hygienePostBookingMessage, DEFAULT_HYGIENE_POST_BOOKING_MESSAGE)
  const confirmationSwitched = shown.confirmationOn !== saved.confirmationOn

  useSettingsSection(`scripting`, {
    dirty: !!draft && (greetingChanged || postBookingChanged || hygieneChanged || confirmationSwitched),
    save: () =>
      agentStore.updateConfig(locationId, {
        ...(greetingChanged && { greeting: shown.greeting.trim() }),
        ...(postBookingChanged && { postBookingMessage: storedPostBooking(shown.postBookingMessage) }),
        ...(hygieneChanged && {
          hygienePostBookingMessage: storedPostBooking(
            shown.hygienePostBookingMessage,
            DEFAULT_HYGIENE_POST_BOOKING_MESSAGE,
          ),
        }),
        ...(confirmationSwitched && { smsConfirmationEnabled: shown.confirmationOn }),
      }),
    discard: () => setDraft(null),
  })

  const reset = (onClick: () => void) => (
    <Button type="button" variant="link" size="xs" onClick={onClick}>
      Reset to default
    </Button>
  )

  return (
    <SectionCard attached title="Scripting">
      <ScriptRow
        label="Greeting on calls"
        action={shown.greeting !== DEFAULT_GREETING && reset(() => setDraft({ ...shown, greeting: DEFAULT_GREETING }))}
        editor={
          <>
            <TokenEditor
              fieldClassName={INPUT_FOCUS}
              ref={greetingEditor}
              value={shown.greeting}
              onChange={(next) => setDraft({ ...shown, greeting: next })}
              labelFor={(token) => GREETING_TOKENS.find((known) => known.key === token)?.label ?? token}
            />
            <InsertBar tokens={GREETING_TOKENS} onInsert={(key) => greetingEditor.current?.insertToken(key)} />
          </>
        }
        preview={renderGreeting(shown.greeting.trim() || DEFAULT_GREETING, businessName, agentName)}
      />

      <ScriptRow
        label="After booking, the agent says"
        action={
          shown.postBookingMessage.trim() !== DEFAULT_POST_BOOKING_MESSAGE &&
          reset(() => setDraft({ ...shown, postBookingMessage: DEFAULT_POST_BOOKING_MESSAGE }))
        }
        editor={
          <>
            <TokenEditor
              fieldClassName={INPUT_FOCUS}
              ref={postBookingEditor}
              value={postBookingToEditor(shown.postBookingMessage)}
              onChange={(text) => setDraft({ ...shown, postBookingMessage: postBookingFromEditor(text) })}
              labelFor={(token) => POST_BOOKING_TAGS.find((part) => part.key === token)?.label ?? token}
            />
            <InsertBar tokens={POST_BOOKING_TAGS} onInsert={(key) => postBookingEditor.current?.insertToken(key)} />
          </>
        }
        preview={renderPostBookingSample(shown.postBookingMessage.trim() || DEFAULT_POST_BOOKING_MESSAGE)}
      />

      <ScriptRow
        label="After booking a hygiene visit, the agent says"
        action={
          shown.hygienePostBookingMessage.trim() !== DEFAULT_HYGIENE_POST_BOOKING_MESSAGE &&
          reset(() => setDraft({ ...shown, hygienePostBookingMessage: DEFAULT_HYGIENE_POST_BOOKING_MESSAGE }))
        }
        editor={
          <>
            <TokenEditor
              fieldClassName={INPUT_FOCUS}
              ref={hygieneEditor}
              value={postBookingToEditor(shown.hygienePostBookingMessage)}
              onChange={(text) => setDraft({ ...shown, hygienePostBookingMessage: postBookingFromEditor(text) })}
              labelFor={(token) => POST_BOOKING_TAGS.find((part) => part.key === token)?.label ?? token}
            />
            <InsertBar tokens={POST_BOOKING_TAGS} onInsert={(key) => hygieneEditor.current?.insertToken(key)} />
          </>
        }
        preview={renderPostBookingSample(
          shown.hygienePostBookingMessage.trim() || DEFAULT_HYGIENE_POST_BOOKING_MESSAGE,
        )}
      />

      <ScriptRow
        label="Booking confirmation text"
        action={
          <span className="flex items-center gap-2">
            <Label htmlFor="sms-confirmation" className="text-body-sm">
              {shown.confirmationOn ? `On` : `Off`}
            </Label>
            <Switch
              id="sms-confirmation"
              checked={shown.confirmationOn}
              onCheckedChange={(on) => setDraft({ ...shown, confirmationOn: on })}
            />
          </span>
        }
        editor={<span className="text-body-sm text-subtle">{CONFIRMATION_HINT}</span>}
        preview={shown.confirmationOn ? preview : undefined}
        note={shown.confirmationOn && `"${OPT_OUT_SUFFIX}" is always added to the end.`}
      />

      <AiAgentPhraseRules locationId={locationId} rules={phraseRules} />
    </SectionCard>
  )
}
